export type ApiResponse<T = unknown> = {
  ok: boolean;
  status: number;
  body: T;
};

const RETRY_STATUSES = new Set([502, 503, 504]);
const RETRY_BACKOFF_MS = [200, 600];

export async function api<T = unknown>(
  path: string,
  init?: RequestInit
): Promise<ApiResponse<T>> {
  // Só GET (ou request sem method) é seguro pra retry. Métodos com efeito
  // colateral (POST/PATCH/DELETE) podem cobrar/duplicar e ficam sem retry.
  const method = (init?.method ?? "GET").toUpperCase();
  const retriable = method === "GET";

  let lastErr: unknown = null;
  for (let attempt = 0; attempt <= (retriable ? RETRY_BACKOFF_MS.length : 0); attempt++) {
    try {
      const res = await fetch(path, {
        credentials: "same-origin",
        ...init,
        headers: {
          ...(init?.body ? { "Content-Type": "application/json" } : {}),
          ...(init?.headers ?? {}),
        },
      });
      if (retriable && RETRY_STATUSES.has(res.status) && attempt < RETRY_BACKOFF_MS.length) {
        await new Promise((r) => setTimeout(r, RETRY_BACKOFF_MS[attempt]));
        continue;
      }
      let body: any = null;
      try {
        body = await res.json();
      } catch {
        body = null;
      }
      return { ok: res.ok, status: res.status, body };
    } catch (err) {
      // network error (offline, DNS, abort, etc.) — só faz sentido tentar de novo em GET
      lastErr = err;
      if (!retriable || attempt >= RETRY_BACKOFF_MS.length) throw err;
      await new Promise((r) => setTimeout(r, RETRY_BACKOFF_MS[attempt]));
    }
  }
  throw lastErr;
}

export function brl(cents: number, currency = "brl"): string {
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: currency.toUpperCase(),
  }).format(cents / 100);
}

export function formatDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("pt-BR", { hour12: false });
}
